import { useState } from 'react';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { CalculatorPanel } from '../components/CalculatorPanel';
import { FeatureCatalogPanel } from '../components/FeatureCatalogPanel';
import { SalesConfigPanel } from '../components/SalesConfigPanel';
import { SalesInvoicesPanel } from '../components/SalesInvoicesPanel';

const TABS = [
  { key: 'calculator', label: 'Calculator' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'price-book', label: 'Price book' },
  { key: 'config', label: 'Assumptions & terms' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

/**
 * Sales pricing calculator & sales-invoice engine (PE-2143, backend PE-2142).
 *
 * TCS-staff only — every `/sales/*` route sits behind osprey-api's
 * CollectiveOnlyGuard plus the watchman SALES_PRICING resource, so this page is
 * gated on SALES_PRICING_VIEW and its writes on SALES_PRICING_MANAGE.
 */
export function SalesPricingPage() {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.SALES_PRICING_MANAGE);

  const [tab, setTab] = useState<TabKey>('calculator');
  // Carries a freshly-saved quote across to the Invoices tab so the user can act
  // on it without hunting for the id.
  const [pendingSelectionId, setPendingSelectionId] = useState<string | null>(null);

  return (
    <div className="py-6 max-w-7xl mx-auto px-2">
      <div className="mb-6">
        <h1 className="text-[23px] font-bold text-foreground">Sales pricing</h1>
        <p className="text-[14px] text-muted-foreground mt-1">
          Build a quote from the price book, freeze it, and raise the sales invoice the prospect
          signs.
        </p>
      </div>

      <div className="border-b border-border mb-6">
        <nav className="flex gap-6" aria-label="Sales pricing sections">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              aria-current={tab === t.key ? 'page' : undefined}
              className={`pb-3 text-sm transition-colors border-b-2 -mb-px ${
                tab === t.key
                  ? 'border-foreground text-foreground font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'calculator' && (
        <CalculatorPanel
          canManage={canManage}
          onSelectionSaved={(selection) => {
            setPendingSelectionId(selection.id);
            setTab('invoices');
          }}
        />
      )}

      {tab === 'invoices' && (
        <SalesInvoicesPanel
          canManage={canManage}
          pendingSelectionId={pendingSelectionId}
          onConsumeSelection={() => setPendingSelectionId(null)}
        />
      )}

      {tab === 'price-book' && <FeatureCatalogPanel canManage={canManage} />}

      {tab === 'config' && <SalesConfigPanel canManage={canManage} />}
    </div>
  );
}
